<script setup>
import { onBeforeUnmount, onBeforeMount, reactive, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import api from '@/services/api.js';
import Swal from 'sweetalert2';
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonCheckbox from "@/components/ArgonCheckbox.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();
const router = useRouter();

const cargando = ref(false);

// --- Estado del Formulario ---
const formulario = reactive({
  username: "", 
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  aceptaTerminos: true
});

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
  store.state.showFooter = false;
  body.classList.remove("bg-gray-100");
  body.classList.add("signup-page"); // Clase temporal para asegurar estilos
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  store.state.showNavbar = true;
  store.state.showSidenav = true;
  store.state.showFooter = true;
  body.classList.add("bg-gray-100");
  body.classList.remove("signup-page");
});

// --- Lógica de Registro con API ---
const manejarRegistro = async () => {
  if (!formulario.aceptaTerminos) {
    Swal.fire("Atención", "Debe aceptar los términos institucionales.", "warning");
    return;
  }

  cargando.value = true;
  try {
    await api.post('registro-solicitud/', {
      username: formulario.username,
      first_name: formulario.first_name,
      last_name: formulario.last_name,
      email: formulario.email,
      password: formulario.password
      // Nota: No enviamos Regional/Gerencia/Seccion/Cargo. Eso lo asigna el Admin al aprobar la solicitud.
    });

    Swal.fire({
      title: 'Solicitud Enviada',
      text: `Gracias ${formulario.first_name}. Su cuenta está pendiente de aprobación y asignación de cargo por el Administrador de TAMEP.`,
      icon: 'success',
      confirmButtonColor: '#003366'
    }).then(() => {
      router.push("/signin");
    });

  } catch (error) {
    let mensaje = "No se pudo enviar la solicitud.";
    if (error.response?.data?.username) mensaje = "El nombre de usuario (o CI) ya existe en el sistema.";
    if (error.response?.data?.email) mensaje = "Este correo ya está registrado.";
    
    Swal.fire("Error en el registro", mensaje, "error");
  } finally {
    cargando.value = false;
  }
};

const irAlLogin = () => router.push("/signin");
</script>

<template>
  <main class="main-content mt-0 app-transition">
    <div
      class="page-header align-items-start min-vh-50 pt-5 pb-11 m-3 border-radius-lg bg-cover-custom"
      style="background-image: url('https://www.tamep.bo/assets/png/1-Ds-k_scH.png'); background-position: center;"
    >
      <span class="mask bg-azul-tamep opacity-7 mask-custom"></span>
      <div class="container position-relative z-index-2">
        <div class="row justify-content-center">
          <div class="col-lg-5 text-center mx-auto mt-5">
            <h1 class="text-white mb-2 text-shadow-custom">Registro Institucional</h1>
            <p class="text-lead text-white text-shadow-custom">
              Sistema de Control de Activos Fijos - TAMEP EP
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="container position-relative z-index-2">
      <div class="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
        <div class="col-xl-5 col-lg-6 col-md-8 mx-auto">
          <div class="card card-custom z-index-0 shadow-lg border-0 mb-5">
            
            <div class="card-header card-header-custom text-center pt-4 pb-2 bg-transparent">
              <i class="fas fa-plane-shield fa-3x text-azul-tamep icon-custom mb-3"></i>
              <h5 class="font-weight-bolder text-title-custom">Crear Solicitud de Acceso</h5>
              <p class="text-xs text-custom-muted mb-0 px-4">Sus datos serán verificados por el departamento de sistemas antes de otorgar el acceso.</p>
            </div>

            <div class="card-body">
              <form role="form" @submit.prevent="manejarRegistro">
                
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-control-label text-xs font-weight-bolder text-custom-muted ms-1">Nombres</label>
                    <argon-input id="firstName" type="text" placeholder="Ej. Juan Carlos" v-model="formulario.first_name" required />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-control-label text-xs font-weight-bolder text-custom-muted ms-1">Apellidos</label>
                    <argon-input id="lastName" type="text" placeholder="Ej. Perez Torrez" v-model="formulario.last_name" required />
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-control-label text-xs font-weight-bolder text-custom-muted ms-1">Identificación de Usuario</label>
                  <argon-input id="username" type="text" placeholder="Usuario (Ej. jperez o Número de CI)" v-model="formulario.username" required />
                </div>
                
                <div class="mb-3">
                  <label class="form-control-label text-xs font-weight-bolder text-custom-muted ms-1">Correo Electrónico Institucional</label>
                  <argon-input id="email" type="email" placeholder="usuario@tamep.bo" v-model="formulario.email" required />
                </div>
                
                <div class="mb-3">
                  <label class="form-control-label text-xs font-weight-bolder text-custom-muted ms-1">Contraseña Segura</label>
                  <argon-input id="password" type="password" placeholder="Mínimo 8 caracteres" v-model="formulario.password" required />
                </div>
                
                <div class="mt-4 mb-2">
                  <argon-checkbox v-model:checked="formulario.aceptaTerminos" class="checkbox-custom">
                    <label class="form-check-label text-sm text-custom-muted" for="flexCheckDefault">
                      Acepto la política de uso de datos institucionales de TAMEP EP.
                    </label>
                  </argon-checkbox>
                </div>

                <div class="text-center mt-4">
                  <argon-button fullWidth class="mb-3 py-3 btn-azul-tamep shadow-lg" type="submit" :disabled="cargando">
                    <i class="fas" :class="cargando ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
                    <span class="ms-2 font-weight-bold">{{ cargando ? 'ENVIANDO SOLICITUD...' : 'ENVIAR SOLICITUD DE REGISTRO' }}</span>
                  </argon-button>
                </div>
                
                <div class="text-center mt-3 pt-3 border-top border-custom-divider">
                  <p class="text-sm mb-0 text-custom-muted">
                    ¿Ya tiene una cuenta aprobada?
                  </p>
                  <a href="javascript:;" @click="irAlLogin" class="text-amarillo-tamep font-weight-bold d-inline-block mt-1">
                    <i class="fas fa-sign-in-alt me-1"></i> Iniciar Sesión
                  </a>
                </div>
              </form>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
/* Transiciones Globales */
.app-transition { transition: all 0.3s ease; }

/* Identidad Visual TAMEP (Tema Claro Default) */
.bg-azul-tamep { background-color: #003366 !important; }
.text-azul-tamep { color: #003366 !important; }
.text-amarillo-tamep { color: #FFCC00 !important; transition: color 0.3s ease;}
.text-amarillo-tamep:hover { color: #e6b800 !important; }
.text-shadow-custom { text-shadow: 0 2px 4px rgba(0,0,0,0.5); }

/* Variables de Color Dinámicas */
.card-custom { background-color: #ffffff; transition: background-color 0.3s ease, box-shadow 0.3s ease; }
.card-header-custom { border-bottom: none; }
.text-title-custom { color: #344767; transition: color 0.3s ease; }
.text-custom-muted { color: #6c757d; transition: color 0.3s ease; }
.icon-custom { transition: color 0.3s ease; }
.border-custom-divider { border-color: #e9ecef !important; transition: border-color 0.3s ease; }

/* Botones */
.btn-azul-tamep {
  background: linear-gradient(310deg, #003366 0%, #00509d 100%);
  color: white;
  border: none;
  transition: all 0.3s ease;
}

.btn-azul-tamep:hover:not(:disabled) {
  background: linear-gradient(310deg, #002244 0%, #003366 100%);
  transform: translateY(-2px);
  box-shadow: 0 7px 14px rgba(0, 51, 102, 0.2), 0 3px 6px rgba(0, 51, 102, 0.1);
}

.btn-azul-tamep:disabled { opacity: 0.7; cursor: not-allowed; }

/* Sobrescribiendo el focus del Argon Input globalmente en este scope */
:deep(.form-control:focus) {
  border-color: #FFCC00 !important;
  box-shadow: 0 0 0 2px rgba(255, 204, 0, 0.2);
}


/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
/* Si la app principal inyecta .dark-mode al body, este componente reacciona */
:global(body.dark-mode) .card-custom { 
  background-color: #1e1e1e !important; 
  box-shadow: 0 10px 30px rgba(0,0,0,0.8) !important; 
  border: 1px solid #333 !important; 
}
:global(body.dark-mode) .text-title-custom { color: #ffffff !important; }
:global(body.dark-mode) .text-custom-muted { color: #aaaaaa !important; }
:global(body.dark-mode) .border-custom-divider { border-color: #333 !important; }

/* El azul oscuro del icono no se ve bien en fondo oscuro, lo aclaramos un poco */
:global(body.dark-mode) .icon-custom.text-azul-tamep { color: #4dabf7 !important; }

/* Sobrescribir inputs de Argon para modo oscuro en la pantalla de Signup */
:global(body.dark-mode) .form-control {
  background-color: #2a2a2a !important;
  color: #e0e0e0 !important;
  border-color: #444 !important;
}
:global(body.dark-mode) .form-control:focus {
  border-color: #FFCC00 !important;
  box-shadow: 0 0 0 2px rgba(255, 204, 0, 0.15) !important;
}
:global(body.dark-mode) .form-control::placeholder { color: #888 !important; }

/* Opcional: Oscurecer un poco más la máscara de la imagen de fondo en Dark Mode */
:global(body.dark-mode) .mask-custom { background-color: #001224 !important; opacity: 0.85 !important; }
</style>